import React from "react";
import { BoxChooser } from "./box_chooser.jsx";
import { boxLabel, request, useBoxAllocation } from "./box_allocation.js";
import { LotIdentity } from "./lot_identity.jsx";
import { lodgedNote } from "./stable_horses.jsx";
import { SaleResult, WithdrawnBanner, isWithdrawn } from "./stable_result.jsx";
import { sydneyDay, sydneyWhen } from "./time.js";

const KINDS = [["xray", "X-rays"], ["scope", "Scope"], ["report", "Report"]];
const link = (url, label) => url ? <a href={url} target="_blank" rel="noopener noreferrer">{label}</a> : null;

function Lodged({ lodged, submissions }) {
  const note = lodgedNote(lodged);
  if (submissions == null) {
    return <p className="caption" data-lodged="unknown">
      {["live", "stale"].includes(lodged.status) ? "What's been lodged for this horse couldn't be read." : note}
    </p>;
  }
  const missing = KINDS.filter(([kind]) => !submissions.some(item => item.mediaType === kind));
  return <>
    {note && <p className="caption">{note}</p>}
    <ul className="lodged-list">
      {submissions.map(item => <li key={item.id} data-media={item.mediaType}>
        <span><strong>{item.submissionType}</strong> <span className={`tag${item.current ? " ok" : ""}`}>{item.current ? "current" : "replaced"}</span></span>
        <span className="caption">Lodged {sydneyWhen(item.lodgedAt)} · {[item.vetName || "Vet not named", item.vetClinic].filter(Boolean).join(", ")}</span>
      </li>)}
      {missing.map(([kind, label]) => <li key={kind} data-media={kind} data-nothing="">
        <strong>{label}</strong> <span className="caption">Nothing lodged</span>
      </li>)}
    </ul>
  </>;
}

// Videos play in the page (owner, 25 Sep 2026), from the catalogue's own host:
// one player, a button per video, and only the metadata loads until play. If a
// browser cannot play one, the page says so and links to it.
function Videos({ videos }) {
  const [current, setCurrent] = React.useState(0);
  const [failed, setFailed] = React.useState(false);
  if (!videos.length) return null;
  const video = videos[current];
  const label = (item, index) => item.name || `Video ${index + 1}`;
  return <div className="stable-video">
    <video key={video.url} src={video.url} controls preload="metadata" playsInline
      aria-label={label(video, current)} onError={() => setFailed(true)}/>
    {failed && <p className="caption">This video can't play here. {link(video.url, "Open it")}</p>}
    {videos.length > 1
      ? <div className="stable-video-pick" role="group" aria-label="Videos">
        {videos.map((item, index) => <button key={item.url} type="button" aria-pressed={index === current}
          className={index === current ? "btn sm primary" : "btn sm"}
          onClick={() => { setCurrent(index); setFailed(false); }}>▶ {label(item, index)}</button>)}
      </div>
      : <p className="caption">{label(video, 0)}</p>}
  </div>;
}

// The catalogue's own text already carries some of what this page used to add
// (seen on dev, Blake Ryan Racing lot 1, 24 Sep 2026): a sire's name can end with its
// country, a barn can already read "Barn K", and dates arrive as ISO text. Each helper
// adds only what is missing, and a value it does not recognise is shown as written.
const withCountry = (name, country) => {
  if (!country) return name || null;
  if (name && name.trim().toUpperCase().endsWith(`(${country.trim().toUpperCase()})`)) return name;
  return [name, `(${country})`].filter(Boolean).join(" ");
};
const barnLabel = barn => barn ? (/^barn\b/i.test(barn.trim()) ? barn.trim() : `Barn ${barn}`) : null;
// A calendar date reads "30 Oct 2024"; an instant with its offset reads in Sydney time.
// A date-time without an offset is left alone: which zone it meant is unknown.
const catalogueDate = value => {
  if (typeof value !== "string") return value || null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return sydneyDay(value);
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/.test(value)) return sydneyWhen(value);
  return value;
};

function selling(c, withdrawn) {
  const session = c.session
    ? [c.session.label || (c.session.day && `Day ${c.session.day}`), catalogueDate(c.session.date), c.session.time].filter(Boolean).join(" · ")
    : null;
  const time = c.ringSellTime ? sydneyWhen(c.ringSellTime) : withdrawn ? "Withdrawn" : "Not published yet";
  // The result is the Sale result card at the top of the page, not a fact here.
  return [["Selling session", session], ["Selling time", time]];
}

function facts(c, withdrawn) {
  return [
    ["Name", c.name],
    ["Foaled", [catalogueDate(c.dateOfBirth), c.countryOfBirth].filter(Boolean).join(" · ")],
    ["Sire", withCountry(c.sire, c.sireCountryOfBirth)],
    ["Dam", [withCountry(c.dam, c.damCountryOfBirth), c.damYearOfFoaling && `foaled ${c.damYearOfFoaling}`].filter(Boolean).join(" ")],
    ["Dam's sire", c.damSire],
    ["Covering stallion", c.coveringStallion],
    ["Pedigree", link(c.pedigreePdfUrl, "Pedigree PDF")],
    ["Pedigree updates", [c.pedigreeUpdates, c.pedigreeUpdatesDate && `(${catalogueDate(c.pedigreeUpdatesDate)})`].filter(Boolean).join(" ")],
    ["Bonus schemes", c.bonusSchemes.join(", ")],
    ["Classification", c.classification ? [
      c.classification.horseClass && `Horse ${c.classification.horseClass}`,
      c.classification.damClass && `Dam ${c.classification.damClass}`,
      c.classification.damProduceClass && `Dam's produce ${c.classification.damProduceClass}`,
    ].filter(Boolean).join(" · ") : null],
    ["Race record", (c.raceRecord.htmlUrl || c.raceRecord.pdfUrl)
      ? <>{link(c.raceRecord.htmlUrl, "Web page")}{c.raceRecord.htmlUrl && c.raceRecord.pdfUrl ? " · " : ""}{link(c.raceRecord.pdfUrl, "PDF")}</>
      : null],
    ["Arrival", catalogueDate(c.arrivalTime)],
    // Where the horse is on the grounds, then how and when it sells, beneath it.
    ["Location", [barnLabel(c.stall.barn), c.stall.primary, c.stall.secondary].filter(Boolean).join(" · ")],
    ...selling(c, withdrawn),
  ].filter(([, value]) => value);
}

// One scrolling page (spec S5): photos and videos, what vets lodged, the box, the
// catalogue. The four-generation pedigree was removed at the owner's request (25 Sep 2026).
// The Shell's back button returns to the Horses view.
export function StableHorse({ sale, vendor = window.VD.access.realm, lotId }) {
  const allocation = useBoxAllocation({ sale, vendor });
  const [horse, setHorse] = React.useState(null);
  const [failure, setFailure] = React.useState("");
  const [shown, setShown] = React.useState(0);
  const [choosing, setChoosing] = React.useState(false);
  const touch = React.useRef(null);
  const path = `/collections/vendor.stable.horse@1/?${new URLSearchParams({ sale, vendor, lot: lotId })}`;

  React.useEffect(() => {
    let active = true;
    request(path).then(value => { if (active) setHorse(value); })
      .catch(problem => {
        if (active) setFailure(problem.status === 404
          ? "This horse isn't one of yours in this sale."
          : "This horse could not be loaded. Refresh to try again.");
      });
    return () => { active = false; };
  }, [path]);

  if (failure) return <p role="alert" className="error-text">{failure}</p>;
  if (!horse) return <p role="status">Loading this horse…</p>;

  const boxed = allocation.lots.find(lot => lot.id === horse.id) || null;
  const canChoose = Boolean(boxed) && !horse.withdrawn && !allocation.locked && allocation.data?.canAssign === true;
  const last = horse.photos.length - 1;
  const swipe = event => {
    const start = touch.current;
    touch.current = null;
    if (start == null) return;
    const moved = event.changedTouches[0].clientX - start;
    if (moved < -40) setShown(index => Math.min(index + 1, last));
    if (moved > 40) setShown(index => Math.max(index - 1, 0));
  };
  const catalogue = horse.catalogue;
  const withdrawn = isWithdrawn(horse, catalogue?.result);
  return <div className="stable-horse">
    <header className="stable-horse-head">
      <h2>Lot {horse.number}</h2>
      <LotIdentity lot={horse}/>
    </header>
    {withdrawn ? <WithdrawnBanner/> : <SaleResult result={catalogue?.result}/>}

    <section className="stable-gallery" aria-label="Photos and videos"
      onTouchStart={event => { touch.current = event.touches[0].clientX; }} onTouchEnd={swipe}>
      {horse.photos.length
        ? <img src={horse.photos[shown]} alt={`Lot ${horse.number}, photo ${shown + 1}`} referrerPolicy="no-referrer"/>
        : <p className="caption">No catalogue photo</p>}
      {horse.photos.length > 1 && <div className="stable-gallery-nav">
        <button type="button" className="btn sm" aria-label="Previous photo" disabled={shown === 0} onClick={() => setShown(shown - 1)}>‹</button>
        <span className="caption">{shown + 1} / {horse.photos.length}</span>
        <button type="button" className="btn sm" aria-label="Next photo" disabled={shown === last} onClick={() => setShown(shown + 1)}>›</button>
      </div>}
      <Videos videos={horse.videos}/>
    </section>

    <section aria-label="Lodged by vets">
      <h3>Lodged by vets</h3>
      <Lodged lodged={horse.lodged} submissions={horse.submissions}/>
    </section>

    <section aria-label="Box">
      <h3>Box</h3>
      <div className="stable-horse-box">
        <span>{!allocation.data
          ? (allocation.loading ? "Loading…" : "Box unavailable")
          : boxed?.box ? `${boxLabel(boxed.box)} · Barn ${boxed.box.barn}` : "Not in a box"}</span>
        {canChoose && <button type="button" className="btn sm" onClick={() => setChoosing(true)}>Change</button>}
      </div>
      {allocation.pending && <button type="button" className="btn sm primary" disabled={allocation.busy}
        onClick={() => allocation.send(allocation.pending)}>Retry same save</button>}
      <p role="status" aria-live="polite" className="muted">{allocation.notice}</p>
      {allocation.error && <p role="alert" className="error-text">{allocation.error}</p>}
    </section>

    <section aria-label="Catalogue">
      <h3>Catalogue</h3>
      <dl className="stable-facts">
        {facts(catalogue, withdrawn).map(([label, value]) => <React.Fragment key={label}><dt>{label}</dt><dd>{value}</dd></React.Fragment>)}
      </dl>
    </section>

    {choosing && boxed && <BoxChooser allocation={allocation} lot={boxed} onClose={() => setChoosing(false)}/>}
  </div>;
}
